import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import { setBackButtonUrl } from "../uiSlice";
import {
  isThemeFlagOn,
  readPlayerLanguage,
} from "../gameSetupStorage";
import { getThemeButtonGradient } from "../../functions/themeAssets";
import { getThemeLangPack } from "../game/snakesLaddersUtils";

export default function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const multiLanguage = isThemeFlagOn(themeData?.MultiLanguage, false);
  const lang = readPlayerLanguage();
  const pack = useMemo(() => getThemeLangPack(themeData, lang), [themeData, lang]);

  useEffect(() => {
    dispatch(
      setBackButtonUrl(multiLanguage ? "/language" : "/login?&save=true")
    );
  }, [dispatch, status, user, multiLanguage]);

  /* Unlock document scroll so logo + Back scroll away with the rules list.
   * Login CSS otherwise keeps body.common-bg overflow:hidden on mobile portrait. */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const snap = [];
    const setImp = (el, prop, value) => {
      if (!el) return;
      snap.push([
        el,
        prop,
        el.style.getPropertyValue(prop),
        el.style.getPropertyPriority(prop),
      ]);
      el.style.setProperty(prop, value, "important");
    };

    html.classList.add("sl-rules-open");
    body.classList.add("sl-rules-open");

    setImp(html, "height", "auto");
    setImp(html, "max-height", "none");
    setImp(html, "overflow-x", "hidden");
    setImp(html, "overflow-y", "auto");
    setImp(html, "overflow", "auto");

    setImp(body, "height", "auto");
    setImp(body, "min-height", "100%");
    setImp(body, "max-height", "none");
    setImp(body, "overflow", "visible");
    setImp(body, "overflow-x", "hidden");
    setImp(body, "overflow-y", "visible");
    setImp(body, "position", "static");
    setImp(body, "background-attachment", "scroll");
    setImp(body, "touch-action", "pan-y");

    setImp(root, "height", "auto");
    setImp(root, "min-height", "100%");
    setImp(root, "max-height", "none");
    setImp(root, "overflow", "visible");

    return () => {
      html.classList.remove("sl-rules-open");
      body.classList.remove("sl-rules-open");
      snap.forEach(([el, prop, value, priority]) => {
        if (value) el.style.setProperty(prop, value, priority || undefined);
        else el.style.removeProperty(prop);
      });
    };
  }, []);

  const rules = Array.isArray(pack.rules)
    ? pack.rules.map((r) => String(r || "").trim()).filter(Boolean)
    : Array.isArray(themeData?.rules)
      ? themeData.rules.map((r) => String(r || "").trim()).filter(Boolean)
      : [];

  const title = pack.rules_title || "Rules";
  const nextLabel = pack.next || "Next";
  const titleColor = themeData?.landing_page_title_color || "#111";
  const { start, end } = getThemeButtonGradient(themeData || {});
  const btnStyle = {
    background: start && end ? `linear-gradient(135deg, ${start}, ${end})` : start || "#e25569",
    color: String(themeData?.button_Textcolor || themeData?.button_text_color || "#fff").trim(),
  };

  if (themeStatus === "loading" && !themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div className="sl-rules-page">
      <h2 className="sl-rules-heading" style={{ ...btnStyle }}>
        {title}
      </h2>
      <ul className="sl-rules-list" style={{ color: titleColor }}>
        {rules.length ? (
          rules.map((rule, i) => (
            <li key={`${i}-${rule.slice(0, 12)}`}>{rule}</li>
          ))
        ) : (
          <li>No rules defined</li>
        )}
      </ul>
      <button
        type="button"
        className="sl-rules-next"
        style={btnStyle}
        onClick={() => navigate("/game")}
      >
        {nextLabel}
      </button>
      <div className="sl-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}
